import {
  lazy,
  Suspense,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useUserDataFlattened } from "../../../modules/user/stores";
import { useQueuedPopup } from "../../../hooks/useQueuedPopup";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import { ActivityType, TimingType } from "@/modules/homeDisplay/types";
import type { GridLotteryPopupResult } from "./useGridLotteryPopup.types";
import { ROUTES } from "../../../utils/navigation";
import {
  useNineGridActivity,
  useActiveNineGridActivity,
  useIsHomeDisplayReady,
  useActivityPopupPriority,
  useIsActivityQualificationAutoEnabled,
} from "../../../modules/homeDisplay/hooks";
import { loadConfig } from "@/modules";
import { useT } from "../../../i18n/hooks";
import congratsBanner from "@/assets/gridLottery/congrats-banner.webp";

const GridLottery = lazy(() =>
  import("../components/GridLottery/GridLottery").then(m => ({
    default: m.GridLottery,
  }))
);

// ✅ 所有活动完成弹窗组件
const AllCompletedDialog: React.FC<{
  visible: boolean;
  onClose: () => void;
}> = ({ visible, onClose }) => {
  const t = useT();
  if (!visible) return null;

  return (
    <div className="all-completed-dialog__overlay" onClick={onClose}>
      <div
        className="all-completed-dialog__content"
        onClick={e => e.stopPropagation()}
      >
        <div className="all-completed-dialog__banner">
          <img src={congratsBanner} alt="Banner" />
          <span className="all-completed-dialog__banner-text">
            {t("gridLottery.congrats")}
          </span>
        </div>
        <div className="all-completed-dialog__body">
          <div className="all-completed-dialog__title">
            {t("gridLottery.allRewardsCollected")}
          </div>
          <div className="all-completed-dialog__subtitle">
            {t("gridLottery.wellDone")}
          </div>
          <button className="all-completed-dialog__button" onClick={onClose}>
            <span>{t("common.ok")}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const GridLotteryContent: React.FC<{ onComplete: () => void }> = ({
  onComplete,
}) => {
  const [visible, setVisible] = useState(true);
  const [showCompletedDialog, setShowCompletedDialog] = useState(false);
  const { data: userInfo } = useUserDataFlattened();
  const gridLotteryActivity = useActiveNineGridActivity();

  // ✅ 弹窗显示时立即标记为已点击（无论是自动还是手动触发）
  useEffect(() => {
    if (gridLotteryActivity && userInfo?.userId) {
      markGridLotteryClickedToday(userInfo.userId, gridLotteryActivity.id);
      // 发送自定义事件通知外部组件更新状态
      window.dispatchEvent(new CustomEvent("gridLotteryOpened"));
    }
  }, [gridLotteryActivity, userInfo?.userId]);

  // ✅ 修改 handleClose，支持接收参数
  const handleClose = (options?: { showCompletedDialog?: boolean }) => {
    setVisible(false);

    if (options?.showCompletedDialog) {
      // 延迟显示完成弹窗，等待九宫格弹窗关闭动画完成
      setTimeout(() => {
        setShowCompletedDialog(true);
      }, 300);
    } else {
      onComplete();
    }
  };

  // ✅ 完成弹窗关闭处理
  const handleCompletedDialogClose = () => {
    setShowCompletedDialog(false);
    loadConfig();
    onComplete();
  };

  return (
    <>
      <Suspense fallback={null}>
        <GridLottery visible={visible} onClose={handleClose} />
      </Suspense>

      {/* ✅ 完成弹窗 - 在九宫格弹窗外部独立显示 */}
      <AllCompletedDialog
        visible={showCompletedDialog}
        onClose={handleCompletedDialogClose}
      />
    </>
  );
};

const GRID_LOTTERY_CONFIG = POPUP_CONFIGS.GRID_LOTTERY;

/**
 * 检查当天是否点击过九宫格活动图标
 */
const hasGridLotteryClickedToday = (
  userId?: string,
  activityId?: string
): boolean => {
  if (!userId || !activityId) return false;
  const today = new Date().toDateString();
  const key = `grid-lottery-clicked:${userId}:${activityId}:${today}`;
  return localStorage.getItem(key) === "true";
};

/**
 * 标记当天已点击过九宫格活动图标
 */
const markGridLotteryClickedToday = (
  userId?: string,
  activityId?: string
): void => {
  if (!userId || !activityId) return;
  const today = new Date().toDateString();
  const key = `grid-lottery-clicked:${userId}:${activityId}:${today}`;
  localStorage.setItem(key, "true");
};

export const useGridLotteryPopup = (): GridLotteryPopupResult => {
  const { data: userInfo, isAuthenticated } = useUserDataFlattened();
  const isConfigReady = useIsHomeDisplayReady();

  const priority = useActivityPopupPriority(
    ActivityType.NINE_GRID_ACTIVITY,
    GRID_LOTTERY_CONFIG.priority,
    TimingType.ACTIVITY_QUALIFICATION
  );
  const isAutoEnabled = useIsActivityQualificationAutoEnabled(
    ActivityType.NINE_GRID_ACTIVITY
  );

  // ✅ 从 activities.NINE_GRID_ACTIVITY 获取九宫格抽奖数据（优先匹配用户正在参与的活动）
  const nineGridActivityList = useNineGridActivity();
  const gridLotteryActivity = useActiveNineGridActivity();

  // ✅ 添加状态追踪是否已点击
  const [hasClicked, setHasClicked] = useState(false);

  // ✅ 初始化和数据变化时重新检查 localStorage
  useEffect(() => {
    if (userInfo?.userId && gridLotteryActivity?.id) {
      const clicked = hasGridLotteryClickedToday(
        userInfo.userId,
        gridLotteryActivity.id
      );
      setHasClicked(clicked);
    }
  }, [userInfo?.userId, gridLotteryActivity?.id]);

  // ✅ 监听弹窗打开事件，更新状态让感叹号消失
  useEffect(() => {
    const handleGridLotteryOpened = () => {
      setHasClicked(true);
    };
    window.addEventListener("gridLotteryOpened", handleGridLotteryOpened);
    return () => {
      window.removeEventListener("gridLotteryOpened", handleGridLotteryOpened);
    };
  }, []);

  // 从活动数据中提取需要的字段（兼容后端字段缺失）
  // ✅ 修改后 - 使用 periodEndTime
  const gridEndMs =
    gridLotteryActivity?.userProgress?.periodEndTime &&
    gridLotteryActivity.userProgress.periodEndTime > Date.now()
      ? gridLotteryActivity.userProgress.periodEndTime
      : Date.now() + 7 * 24 * 60 * 60 * 1000; // 默认7天有效期

  // ✅ 判断是否有待领取的任务奖励
  const hasClaimableTask = useMemo(() => {
    const nextTaskInfo = gridLotteryActivity?.userProgress?.nextTaskInfo;
    if (!nextTaskInfo) return false;

    // 如果当前累计充值 >= 任务目标，说明任务完成可领取
    return (
      nextTaskInfo.currentCumulativeAmount >=
      nextTaskInfo.actualCumulativeAmount
    );
  }, [gridLotteryActivity]);

  // claimableCount: 只在有待领取任务时为1，否则为0
  const claimableCount = hasClaimableTask ? 1 : 0;

  // ✅ 徽章显示逻辑
  const shouldShowBadge = useMemo(() => {
    if (!gridLotteryActivity) return false;

    // 条件1：有待领取的任务奖励 → 显示数字 1
    if (hasClaimableTask) {
      return true;
    }

    // 条件2：当天第一次看到活动（未点击过）→ 显示感叹号
    if (!hasClicked) {
      return true;
    }

    return false;
  }, [gridLotteryActivity, hasClaimableTask, hasClicked]);

  // 是否显示弹窗
  const shouldShow =
    isConfigReady &&
    isAuthenticated &&
    !!gridLotteryActivity &&
    nineGridActivityList.length > 0;

  const { trigger: originalTrigger } = useQueuedPopup({
    popupId: GRID_LOTTERY_CONFIG.id,
    priority,
    shouldShow: () =>
      isConfigReady && isAuthenticated && nineGridActivityList.length > 0,
    renderContent: onComplete => <GridLotteryContent onComplete={onComplete} />,
    showOnce: true,
    allowManualTrigger: true,
    autoTrigger: isAutoEnabled,
    allowedPaths: [
      ROUTES.HOME,
      ROUTES.HOME_ALT,
      "/",
      ROUTES.EVENT_CENTER,
      ROUTES.LOGIN,
      ROUTES.REGISTER,
    ],
    dependencies: [
      nineGridActivityList,
      isAuthenticated,
      isConfigReady,
      isAutoEnabled,
    ],
    timingType: TimingType.ACTIVITY_QUALIFICATION,
    userBinding: true,
    getUserId: () => userInfo?.userId,
  });

  // ✅ 包装 trigger，在点击时标记为已点击
  const trigger = useCallback(() => {
    if (gridLotteryActivity) {
      markGridLotteryClickedToday(userInfo?.userId, gridLotteryActivity.id);
      setHasClicked(true); // 手动点击时也更新状态
    }
    originalTrigger();
  }, [originalTrigger, gridLotteryActivity, userInfo?.userId]);

  // ✅ 使用 useMemo 计算剩余时间，确保能响应数据变化
  const remainingMs = useMemo(() => {
    if (gridEndMs === 0) return 0;
    const remaining = gridEndMs - Date.now();
    return Math.max(0, remaining);
  }, [gridEndMs, gridLotteryActivity?.userProgress?.periodEndTime]);

  return {
    shouldShow,
    trigger,
    remainingMs,
    shouldShowBadge,
    claimableCount,
    landingPageTitle:
      gridLotteryActivity?.activityEntryPoint?.landingPageTitle ||
      (gridLotteryActivity as any)?.entryPoint?.landingPageTitle,
  };
};
